/**
 * Przeszkody na mapie wyprawy (drzewa, groby, skały). Silnik decyduje, gdzie stoją i jakie są duże;
 * render stawia w tych miejscach pasujące modele. Wszystko wynika z seeda wyprawy, więc ten sam
 * fragment świata wygląda i blokuje tak samo — bez przechowywania mapy w stanie gry.
 */

/** Bok kawałka mapy w jednostkach świata (= 16 jednostek sceny przy skali 1/24). */
export const OBSTACLE_CHUNK = 384;
/** Promienie kolizji: mała, średnia i duża przeszkoda. */
export const OBSTACLE_RADII = [14, 26, 44] as const;
/** Wokół punktu startu jest pusto — nie zaczynamy wyprawy wciśnięci w drzewo. */
const CLEAR_START = 260;

export interface Obstacle {
  x: number;
  y: number;
  radius: number;
  /** 0 = mała, 1 = średnia, 2 = duża. */
  size: 0 | 1 | 2;
  /** 0..1 — render wybiera nim konkretny model danej wielkości. */
  variant: number;
}

/** Deterministyczny los 0..1 dla (seed, kawałek, indeks) — wspólny dla elementów mapy. */
export function hash(seed: number, x: number, y: number, i: number): number {
  let h = (seed ^ Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(i, 1274126177)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = Math.imul(h ^ (h >>> 16), 2246822519);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

const cache = new Map<string, Obstacle[]>();

/** Przeszkody w jednym kawałku mapy. `count` = ile prób postawienia przeszkody (gęstość strefy). */
export function chunkObstacles(seed: number, cx: number, cy: number, count: number): Obstacle[] {
  const key = `${seed}:${count}:${cx},${cy}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const out: Obstacle[] = [];
  for (let i = 0; i < count; i++) {
    const roll = hash(seed, cx, cy, i * 5 + 3);
    const size: 0 | 1 | 2 = roll < 0.5 ? 0 : roll < 0.85 ? 1 : 2;
    const radius = OBSTACLE_RADII[size];
    // Margines od krawędzi kawałka: przeszkody z sąsiednich kawałków na siebie nie nachodzą.
    const m = OBSTACLE_RADII[2];
    const x = cx * OBSTACLE_CHUNK + m + hash(seed, cx, cy, i * 5) * (OBSTACLE_CHUNK - 2 * m);
    const y = cy * OBSTACLE_CHUNK + m + hash(seed, cx, cy, i * 5 + 1) * (OBSTACLE_CHUNK - 2 * m);
    if (Math.hypot(x, y) < CLEAR_START + radius) continue;
    // Między przeszkodami zostaje przejście szersze niż duży wróg.
    if (out.some((o) => Math.hypot(o.x - x, o.y - y) < o.radius + radius + 70)) continue;
    out.push({ x, y, radius, size, variant: hash(seed, cx, cy, i * 5 + 2) });
  }
  if (cache.size > 4000) cache.clear();
  cache.set(key, out);
  return out;
}

/** Przeszkody, które mogą dotknąć okręgu (x, y, r). */
export function obstaclesNear(seed: number, count: number, x: number, y: number, r: number, out: Obstacle[] = []): Obstacle[] {
  out.length = 0;
  if (count <= 0) return out;
  const reach = r + OBSTACLE_RADII[2];
  const x0 = Math.floor((x - reach) / OBSTACLE_CHUNK);
  const x1 = Math.floor((x + reach) / OBSTACLE_CHUNK);
  const y0 = Math.floor((y - reach) / OBSTACLE_CHUNK);
  const y1 = Math.floor((y + reach) / OBSTACLE_CHUNK);
  for (let cx = x0; cx <= x1; cx++) {
    for (let cy = y0; cy <= y1; cy++) {
      for (const o of chunkObstacles(seed, cx, cy, count)) {
        if (Math.abs(o.x - x) < reach && Math.abs(o.y - y) < reach) out.push(o);
      }
    }
  }
  return out;
}

/** Wypycha okrąg (ciało) z przeszkód. Ruch „ślizga się” po ich krawędzi zamiast stawać w miejscu. */
export function pushOutOfObstacles(body: { x: number; y: number }, radius: number, near: readonly Obstacle[]): void {
  for (const o of near) {
    const dx = body.x - o.x;
    const dy = body.y - o.y;
    const d = Math.hypot(dx, dy);
    const min = o.radius + radius;
    if (d >= min) continue;
    if (d < 1e-6) {
      body.x = o.x + min;
      continue;
    }
    body.x = o.x + (dx / d) * min;
    body.y = o.y + (dy / d) * min;
  }
}
